// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { AppState } from '../../../../../../core/reducers';
// CRUD
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
// Services and Models
import {
  Drug,
  DrugActionTypes,
  DrugGroupService,
  DrugOnServerCreated,
  DrugOnServerUpdated,
  DrugService,
  selectDrugError,
  selectDrugLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip, switchMap } from 'rxjs/operators';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { GeneralValidationMessagesService } from '../../../../../../core/services/general-validation-messages.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PictureCropperComponent } from '../../../../../partials/content/general/picture-cropper/picture-cropper.component';
import { ImageSettingService } from '../../../../../../core/services/image-setting.service';
import { ZoomInEntityComponent } from '../../../../../partials/content/general/zoom-in-entity/zoom-in-entity.component';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-drug-edit',
  templateUrl: './drug-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrugEditComponent implements OnInit, OnDestroy {
  drug: Drug;
  oldDrug: Drug;
  loadingSubject = new BehaviorSubject<boolean>(true);
  drugForm: FormGroup;
  hasFormErrors = false;
  emptyDrugGroup = false;
  isViewMode: boolean;
  filteredGroups: Observable<any[]>;
  private headerMargin: number;
  private subscriptions: Subscription[] = [];
  drugGroupAutocompleteCall = this.drugGroupService.searchDrugGroups.bind(this.drugGroupService);
  @ViewChild('drugGroupAutocomplete') drugGroupAutocomplete: GeneralAutocompleteComponent;
  params: any;
  view = false;
  loadingData = false;
  drugDefault = '/assets/media/drugs/defualtDrug.jpg';
  drugProfile;
  newAvatar = null;
  needToChangeImage = false;
  basicInfo: any = {};
  needToChange = false;

  constructor(
    private navigationService: NavigationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private drugFB: FormBuilder,
    private drugGroupService: DrugGroupService,
    private drugService: DrugService,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private globalService: GeneralValidationMessagesService,
    private dialog: MatDialog,
    private imageSettingService: ImageSettingService,
  ) {}

  /**
   * On init
   */
  ngOnInit() {
    // this line due to make the navigation service return to drugs list if there is no ->
    // navigation stack saved before in special cases
    this.navigationService.setDefaultRoot('/lists/drugs');

    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
    });
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      if (!res) {
        this.router.navigateByUrl('/lists/drugs');
      }
    });
    this.subscriptions.push(userSub);
    const errorSubscription = this.store.pipe(select(selectDrugError), skip(1)).subscribe((res) => {
      if (res) {
        this.layoutUtilsService.showActionNotification(
          this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
          MessageType.Update,
          3000,
          true,
          false,
        );
        this.loadingSubject.next(false);
      }
    });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectDrugLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          switch (res.action_type) {
            case DrugActionTypes.DrugCreated:
              this.layoutUtilsService.showActionNotification(
                this.translate.instant('DRUG.EDIT.ADD_MESSAGE'),
                MessageType.Create,
                3000,
                true,
                false,
              );
              this.goBack();
              break;
            case DrugActionTypes.DrugUpdated:
              if (!this.needToChangeImage) {
                this.layoutUtilsService.showActionNotification(
                  this.translate.instant('DRUG.EDIT.UPDATE_MESSAGE'),
                  MessageType.Update,
                  5000,
                  false,
                  false,
                );
                this.goBack();
              } else {
                this.globalService.getEntitiesById('drugs', this.drug.id).subscribe((drug) => {
                  if (drug?.data) {
                    this.needToChangeImage = false;
                    this.loadDrug(drug.data);
                    this.layoutUtilsService.showActionNotification(
                      'Drug picture changed successfully',
                      MessageType.Update,
                      3000,
                      false,
                      false,
                    );
                    this.cdr.detectChanges();
                    return;
                  }
                });
              }
              break;
          }
        }
      });
    this.subscriptions.push(successSubscription);
    this.loadingSubject.next(true);
    const routeSub = this.activatedRoute.params.subscribe((params) => {
      const id = params.id;
      if (id && id > 0) {
        const drugById = this.globalService.getEntitiesById('drugs', id).subscribe((res) => {
          if (res) {
            this.loadDrug(res.data);
            this.cdr.detectChanges();
            return;
          }
        });
        this.subscriptions.push(drugById);
      } else {
        this.loadDrug(new Drug());
      }
      this.cdr.detectChanges();
    });
    this.subscriptions.push(routeSub);
    // sticky portlet header
    window.onload = () => {
      const style = getComputedStyle(document.getElementById('kt_header'));
      this.headerMargin = parseInt(style.height, 0);
    };
  }

  loadDrug(drug) {
    if (!drug) {
      this.goBack();
      return;
    }
    this.drug = drug;
    this.oldDrug = Object.assign({}, drug);
    this.initDrug();
    this.basicInfo = {
      avatar: this.drug?.pre_signed_url?.url ? this.drug.pre_signed_url.url : this.drugDefault,
    };
    this.drugProfile = this.basicInfo.avatar ? this.basicInfo.avatar : this.drugDefault;
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init drug
   */
  initDrug() {
    this.createForm();
    this.loadingSubject.next(false);
    if (!this.drug.id) {
      this.isViewMode = false;
    } else this.isViewMode = this.activatedRoute.url[`_value`][1]?.path === 'view';
  }

  /**
   * Create form
   */
  createForm() {
    this.drugForm = this.drugFB.group({
      drug: [this.drug.drug, Validators.required],
      strength: [this.drug.strength],
      unit: [this.drug.unit],
      ndc: [this.drug.ndc],
      brand_generic_flag: [this.drug.brand_generic_flag ? this.drug.brand_generic_flag : false],
      brand: [this.drug.brand],
      generic: [this.drug.generic],
      th_class: [this.drug.th_class],
      cfi: [this.drug.cfi],
      drug_schedule: [this.drug.drug_schedule],
      otc_flag: [this.drug.otc_flag ? this.drug.otc_flag : false],
      upc_code: [this.drug.upc_code],
      processed: [this.drug.processed],
      hicl_seqno: [this.drug.hicl_seqno],
      pack_size: [this.drug.pack_size],
      jcode: [this.drug.jcode],
      gpi: [this.drug.gpi],
      rxcui: [this.drug.rxcui],
      ddid: [this.drug.ddid],
      manufacturer: [this.drug.manufacturer],
      form: [this.drug.form],
      drug_group: [this.drug.drug_group],
      note: [this.drug.note, Validators.maxLength(1000)],
      is_active: [this.drug.is_active ? this.drug.is_active : false],
    });
    if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.drugForm.disable();
      this.view = true;
    } else {
      this.view = false;
    }
    this.loadingData = true;
    const autocompleteSub = this.drugForm
      .get('drug_group')
      .valueChanges.pipe(
        switchMap((value) =>
          value && value.length >= 3 ? this.drugGroupService.searchDrugGroups(value) : of([]),
        ),
      )
      .subscribe((result) => {
        this.filteredGroups = result.data ? of(result.data) : of([]);
        this.cdr.detectChanges();
      });
    this.subscriptions.push(autocompleteSub);
    this.drugForm.controls.drug_group.setValue(this.drug.drug_group);
    this.drugGroupAutocomplete?.setValue(this.drug.drug_group);
    this.cdr.detectChanges();
  }

  goBack() {
    this.loadingSubject.next(false);
    this.navigationService.goBack();
  }

  /**
   * Reset
   */
  reset() {
    this.drug = Object.assign({}, this.oldDrug);
    this.createForm();
    this.drugForm.controls[`entity_type`]?.enable();
    this.hasFormErrors = false;
    this.drugForm.markAsPristine();
    this.drugForm.markAsUntouched();
    this.drugForm.updateValueAndValidity();
  }

  submit() {
    this.drugGroupAutocomplete.updateValueAndValidity();
    this.hasFormErrors = false;
    const controls = this.drugForm.controls;
    /** check form */
    if (this.drugForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      return;
    }
    // tslint:disable-next-line:prefer-const
    const editedDrug = this.prepareDrug();
    if (editedDrug.id > 0) {
      this.updateDrug(editedDrug);
      return;
    }
    this.addDrug(editedDrug);
  }

  /**
   * Returns object for saving
   */
  prepareDrug(): Drug {
    const controls = this.drugForm?.controls;
    const _drug = new Drug();
    _drug.id = this.drug?.id;
    _drug.drug = controls.drug.value;
    _drug.strength = controls.strength.value;
    _drug.unit = controls.unit.value;
    _drug.ndc = controls.ndc.value;
    _drug.brand_generic_flag = controls.brand_generic_flag.value;
    _drug.brand = controls.brand.value;
    _drug.generic = controls.generic.value;
    _drug.th_class = controls.th_class.value;
    _drug.cfi = controls.cfi.value;
    _drug.drug_schedule = controls.drug_schedule.value;
    _drug.otc_flag = controls.otc_flag.value;
    _drug.upc_code = controls.upc_code.value;
    _drug.processed = controls.processed.value;
    _drug.hicl_seqno = controls.hicl_seqno.value;
    _drug.pack_size = controls.pack_size.value;
    _drug.jcode = controls.jcode.value;
    _drug.gpi = controls.gpi.value;
    _drug.rxcui = controls.rxcui.value;
    _drug.ddid = controls.ddid.value;
    _drug.manufacturer = controls.manufacturer.value;
    _drug.form = controls.form.value;
    _drug.drug_group = controls.drug_group.value;
    _drug.note = controls.note.value;
    _drug.is_active = controls.is_active.value;
    if (this.needToChangeImage || this.needToChange) {
      _drug.avatar = this.basicInfo.avatarUUID;
    } else {
      _drug.avatar = this.drug.avatar;
    }
    for (const [key, value] of Object.entries(_drug)) {
      if (typeof value === 'string') {
        _drug[key] = value.trim();
      }
    }
    return _drug;
  }

  /**
   * Add drug
   *
   * @param _drug
   */
  addDrug(_drug: Drug) {
    this.loadingSubject.next(true);
    this.store.dispatch(new DrugOnServerCreated({ drug: _drug }));
  }

  /**
   * Update drug
   *
   * @param _drug
   */
  updateDrug(_drug: Drug) {
    this.loadingSubject.next(true);

    const updateDrug: Update<Drug> = {
      id: _drug.id,
      changes: _drug,
    };

    this.store.dispatch(
      new DrugOnServerUpdated({
        partialDrug: updateDrug,
        drug: _drug,
      }),
    );
  }

  /**
   * Checking control validation
   *
   * @param controlName
   * @param validationType
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.drugForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  /**
   * Close alert
   *
   */
  onAlertClose() {
    this.hasFormErrors = false;
    this.emptyDrugGroup = false;
  }

  /**
   * Rerurns status string
   */
  getStatusString(): string {
    switch (this.drugForm.get('is_active').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  editDrug(id) {
    this.navigationService.navigateTo('lists/drugs/edit/' + id);
  }

  filterDrugGroupFn(event: any): string {
    return event ? event.name : '';
  }

  onDrugGroupChange(val) {
    this.drugForm.controls.drug_group.setValue(val);
    this.drugForm.controls.drug_group.updateValueAndValidity();
  }
  updateProfile() {
    this.drugGroupAutocomplete.updateValueAndValidity();
    this.emptyDrugGroup = false;
    const controls = this.drugForm.controls;
    /** check form */
    if (this.drugForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.emptyDrugGroup = true;
      return;
    }
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.height = '400px';
    const defaultImage = this.drugDefault;
    if (this.drug.id) {
      this.globalService.getEntitiesById('drugs', this.drug.id).subscribe((drug) => {
        if (drug?.data) {
          dialogConfig.data = {
            existImage: drug?.data?.pre_signed_url?.url,
            mode: 'UPDATE',
            defaultImage,
          };
          const dialogRef = this.dialog.open(PictureCropperComponent, dialogConfig);
          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              switch (result.mode) {
                case 'DELETE': {
                  this.needToChangeImage = true;
                  const editedDrug = this.prepareDrug();
                  editedDrug.avatar = null;
                  if (editedDrug.id > 0) {
                    this.updateDrug(editedDrug);
                    return;
                  }
                  break;
                }
                case 'UPDATE': {
                  if (result.img) {
                    this.newAvatar = this.imageSettingService.dataURLtoFile(result.img, 'fileName');
                    this.needToChangeImage = true;
                    const reader = new FileReader();
                    reader.readAsDataURL(this.newAvatar);
                    reader.onloadend = () => {
                      this.basicInfo.avatar = reader.result;
                      this.cdr.detectChanges();
                    };
                    let editedDrug = this.prepareDrug();
                    if (editedDrug.id > 0) {
                      this.updateDrugImage(() => {
                        editedDrug = this.prepareDrug();
                        if (editedDrug.id > 0) {
                          this.updateDrug(editedDrug);
                          return;
                        }
                        return null;
                      });
                      return;
                    }
                  }
                  break;
                }
              }
            }
          });
        }
      });
    } else {
      dialogConfig.data = { existImage: null, mode: 'UPDATE', defaultImage };
      const dialogRef = this.dialog.open(PictureCropperComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          switch (result.mode) {
            case 'UPDATE': {
              if (result.img) {
                this.newAvatar = this.imageSettingService.dataURLtoFile(result.img, 'fileName');
                this.drugProfile = result.img;
                this.needToChangeImage = true;
                const reader = new FileReader();
                reader.readAsDataURL(this.newAvatar);
                reader.onloadend = () => {
                  this.basicInfo.avatar = reader.result;
                  this.cdr.detectChanges();
                };
              }
              break;
            }
          }
        }
      });
    }
  }
  private updateDrugImage(cc) {
    if (this.needToChangeImage) {
      return this.drugService.updateDrugImage(this.newAvatar, this.drug.id).subscribe((res2) => {
        this.drugService.uploadDrugImage(res2, this.newAvatar).subscribe(() => {
          this.basicInfo.avatarUUID = res2.data.uuid;
          cc();
        });
      });
    } else {
      cc();
    }
  }
  showDrugPhoto(drug) {
    if (!this.drug.pre_signed_url?.url) {
      return;
    }
    this.dialog.open(ZoomInEntityComponent, { data: { entity: drug }, maxWidth: '600px' });
  }
}
